import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useBreakpoint, Icon, useGoogleMapsPlacesBff } from '@panel/commons';
import { isKeyCodeNumber, isKeyCodeNumpadNumber, isSpecialKey } from '@panel/commons/helpers/keyUtils';
import { site } from '@panel/commons/helpers';
import { Step1Module2Selectors, Step1Module2Operations } from '../../../ducks/step1Module2';
import { ComboLocation } from './ComboLocation';
import {
  Container,
  Title,
  InputTitle,
  ErrorMessage,
  AddressContainer,
  Address,
  PlacesSuggestions,
  LocationCombosContainer,
  InputCEP,
  SearchButton,
  CepContainer,
  InputComplement,
  AddressFieldsContainer,
  ComplementContainer
} from './styles';
import { sendEvent } from '../../../utils/trackingEvent';
import { formatCep } from './utils';

const siteId = site.id;
const hasNoSubDistrict = ['PLEC', 'CAPA', 'LLVE'];
const hasCep = ['IWBR', 'WIBR', 'ICBR'].includes(siteId);
const addressSeparator = ['IWBR', 'WIBR', 'ICBR'].includes(siteId) ? '-' : ',';
const showSubDistrict = hasNoSubDistrict.indexOf(siteId) !== -1;

const countriesIds = {
  '24MX': { countryCode: '18', gmapCode: 'mx' },
  DVPE: { countryCode: '11', gmapCode: 'pe' },
  UBPE: { countryCode: '111', gmapCode: 'pe' },
  PLEC: { countryCode: '9', gmapCode: 'ec' },
  CAPA: { countryCode: '20', gmapCode: 'pa' },
  LLVE: { countryCode: '13', gmapCode: 've' },
  ZPAR: { countryCode: '1', gmapCode: 'ar' },
  ICBR: { countryCode: '2', gmapCode: 'br' },
  IWBR: { countryCode: '2', gmapCode: 'br' },
  WIBR: { countryCode: '2', gmapCode: 'br' },
  VAMX: { countryCode: '36', gmapCode: 'mx' }
};

export const LocationSection = ({
  register,
  errors,
  isLoaded,
  loadError,
  setValue,
  getValues,
  clearErrors,
  trigger,
  reset
}) => {
  const [addressValue, setAddressValue] = useState('');
  const [addressValidation, setAddressValidation] = useState(null);
  const [getSuggestions, setGetSuggestions] = useState(true);
  const [isAddressFocused, setIsAddressFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const [reachedBreakPoint] = useBreakpoint(1280);
  const [siteCodes, setSiteCodes] = useState({});
  const [searchWithCep, setSearchWithCep] = useState(false);
  const skipBlurRef = useRef(false);
  const placesOptions = useMemo(
    () => ({
      componentRestrictions: siteCodes.useGmapCode ? { country: siteCodes.useGmapCode } : undefined,
      isScriptLoaded: isLoaded
    }),
    [siteCodes.useGmapCode, isLoaded]
  );
  const {
    ready: placesReady,
    loading: placesLoading,
    predictions,
    search: searchPlaces,
    getDetails,
    geocode,
    resetSession
  } = useGoogleMapsPlacesBff(placesOptions);
  const { isEdited, hasCoordinates, formValues, cep, cepError, loadingDistrict, addressComplement } = useSelector(
    Step1Module2Selectors.getStep1Module2State
  );
  const [breakMobile] = useBreakpoint(480);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    address,
    selectedProvince,
    selectedCity,
    selectedDistrict,
    selectedSubDistrict,
    provinceData,
    cityData,
    districtData,
    subDistrictData,
    locationInfo
  } = formValues;
  const cepDefault = getValues('cep') ? formatCep(getValues('cep')) : formatCep(cep);

  const setIsEdited = value => {
    if (isEdited !== value) {
      dispatch(Step1Module2Operations.setIsEdited(value));
    }
  };

  useEffect(() => {
    if (countriesIds[siteId]) {
      setSiteCodes({ useCountryId: countriesIds[siteId].countryCode, useGmapCode: countriesIds[siteId].gmapCode });
      dispatch(Step1Module2Operations.getLocation(`V1-A-${countriesIds[siteId].countryCode}`, 'provinceData'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (address) {
      const [shortAddress] = address.split(addressSeparator);
      setAddressValue(shortAddress);
      setValue('addressVal', shortAddress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, isEdited, setValue]);

  useEffect(() => {
    if (addressComplement && getValues('addressComplement') !== addressComplement) {
      setValue('addressComplement', addressComplement);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressComplement]);

  useEffect(() => {
    if (locationInfo && !isEdited) {
      if (locationInfo.idprovincia) {
        const getLocationData = provinceData.find(province => province.location_id === locationInfo.idprovincia);
        dispatch(Step1Module2Operations.setSelectedProvince(getLocationData, null));
      }
      if (locationInfo.idciudad) {
        const getLocationData = cityData.find(city => city.location_id === locationInfo.idciudad);
        dispatch(Step1Module2Operations.setSelectedCity(getLocationData, null));
      }
      if (locationInfo.idzonaciudad) {
        const getLocationData = districtData.find(district => district.location_id === locationInfo.idzonaciudad);
        dispatch(Step1Module2Operations.setSelectedDistrict(getLocationData, null));
      }
      if (locationInfo.idsubzonaciudad) {
        const getLocationData = subDistrictData.find(
          subdistrict => subdistrict.location_id === locationInfo.idsubzonaciudad
        );
        dispatch(Step1Module2Operations.setSelectedSubDistrict(getLocationData));
      }
    }
  }, [cityData, dispatch, districtData, isEdited, locationInfo, provinceData, subDistrictData]);

  const setLocationByCoords = locationValues => {
    const { idprovincia, idciudad, idzonaciudad } = locationValues;
    if (idprovincia) {
      dispatch(Step1Module2Operations.resetValues());
      setIsEdited(false);
      dispatch(Step1Module2Operations.getLocation(idprovincia, 'cityData'));
    }
    if (idciudad) {
      dispatch(Step1Module2Operations.getLocation(idciudad, 'districtData'));
    }
    if (idzonaciudad) {
      dispatch(Step1Module2Operations.getLocation(idzonaciudad, 'subDistrictData'));
    }
  };

  useEffect(() => {
    if (isLoaded && locationInfo && isEdited === false) {
      setLocationByCoords(locationInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationInfo, isLoaded, isEdited]);

  const handleOnError = (status, clearSuggestions) => {
    if (status === 'ZERO_RESULTS') {
      setAddressValidation(
        <ErrorMessage>
          {reachedBreakPoint
            ? t('publisher.step1.module2.addressNotFound.d')
            : t('publisher.step1.module2.addressNotFound.m')}
        </ErrorMessage>
      );
      clearSuggestions();
    }
  };

  const setAddress = value => {
    dispatch(Step1Module2Operations.setAddress(value));
  };

  const setCep = value => {
    const formattedCep = formatCep(value);
    if (formattedCep) {
      setValue(formattedCep);
      dispatch(Step1Module2Operations.setCep(formattedCep));
    }
  };

  const setAddressComplement = value => {
    if (value) {
      dispatch(Step1Module2Operations.setAddressComplement(value));
    } else if (!value && getValues('addressComplement') === '') {
      dispatch(Step1Module2Operations.setAddressComplement(''));
    }
  };

  const setDefaultLocation = (city, district, subDistrict) => {
    if (subDistrict && subDistrict.latitude && subDistrict.longitude) {
      dispatch(Step1Module2Operations.setCoordinates({ lat: subDistrict.latitude, lng: subDistrict.longitude }));
    } else if (district && district.latitude && district.longitude) {
      dispatch(Step1Module2Operations.setCoordinates({ lat: district.latitude, lng: district.longitude }));
    } else if (city && city.latitude && city.longitude) {
      dispatch(Step1Module2Operations.setCoordinates({ lat: city.latitude, lng: city.longitude }));
    } else if (selectedProvince && selectedProvince.latitude && selectedProvince.longitude) {
      dispatch(
        Step1Module2Operations.setCoordinates({ lat: selectedProvince.latitude, lng: selectedProvince.longitude })
      );
    }
  };

  const getLocationWithoutSuggestion = async (city, district = null, subdistrict = null) => {
    try {
      let searchAddress = `${addressValue} ${selectedProvince.name.split('(')[0]} ${city.name}`;
      if (district) {
        searchAddress = `${searchAddress} ${district.name}`;
      }
      if (subdistrict) {
        searchAddress = `${searchAddress} ${subdistrict.name}`;
      }
      const geocodeOptions = siteCodes.useGmapCode ? { componentRestrictions: { country: siteCodes.useGmapCode } } : {};
      const { results } = await geocode(searchAddress, geocodeOptions);
      if (results.length > 0) {
        const loc = results[0].geometry?.location;
        const latLng = loc ? { lat: loc.lat(), lng: loc.lng() } : undefined;
        dispatch(Step1Module2Operations.setCoordinates(latLng));
      } else {
        return setDefaultLocation(city, district, subdistrict);
      }
    } catch {
      setDefaultLocation(city, district, subdistrict);
    } finally {
      resetSession();
    }
  };

  useEffect(() => {
    if (searchWithCep && selectedCity && !loadingDistrict) {
      getLocationWithoutSuggestion(selectedCity, selectedDistrict, selectedSubDistrict);
      setSearchWithCep(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchWithCep, selectedDistrict, selectedCity, selectedSubDistrict, loadingDistrict, setSearchWithCep]);

  const handleUnSelectSuggestions = async value => {
    setGetSuggestions(false);
    const cepVal = getValues('cep');
    const [shortAddress] = address ? address.split(addressSeparator) : '';
    if (addressValue !== shortAddress) {
      if (value) {
        sendEvent('Principales Modulo2', 'No Sugerencia');
      }
      setIsEdited(true);
      if ((cepVal !== undefined && cepVal === '') || cepVal === undefined) {
        dispatch(Step1Module2Operations.resetValues());
      }
      setAddress(value);
      if (addressValue && cepVal !== undefined && cepVal !== '') {
        getLocationWithoutSuggestion(selectedCity, selectedProvince, selectedDistrict, selectedSubDistrict);
      }
    }
  };

  const handleSelectSuggestions = async (value, select = false, placeId) => {
    const cepVal = getValues('cep');
    if ((select && cepVal === undefined) || (select && cepVal !== undefined && cepVal === '')) {
      if (!placeId) {
        handleUnSelectSuggestions(value);
        return false;
      }
      try {
        const details = await getDetails(placeId);
        const getCoordinates = { lat: details.lat, lng: details.lng };
        setGetSuggestions(false);
        setAddress(value);
        setAddressValue(value.split(addressSeparator)[0]);
        setValue('addressVal', value.split(addressSeparator)[0]);

        const dataCoords = await dispatch(Step1Module2Operations.getLatLngData(getCoordinates.lat, getCoordinates.lng));
        if (dataCoords.data[0] !== undefined) {
          setLocationByCoords(dataCoords.data[0]);
          clearErrors();
          sendEvent('Principales Modulo2', 'Sugerencia');
          resetSession();
          return true;
        }
        dataCoords.data[0] = [];
        dispatch(Step1Module2Operations.resetValues());
        resetSession();
        return false;
      } catch {
        resetSession();
        return false;
      }
    } else {
      handleUnSelectSuggestions(value);
      return false;
    }
  };

  const getCep = async () => {
    const CEPVal = getValues('cep');
    setCep(CEPVal);
    const dataCoords = await dispatch(Step1Module2Operations.getCep(CEPVal));
    if (dataCoords !== undefined && dataCoords.data !== undefined) {
      setLocationByCoords(dataCoords.data);
      if (addressValue) {
        setSearchWithCep(true);
      } else {
        dispatch(Step1Module2Operations.setCoordinates({ lat: '', lng: '' }));
      }
    }
  };
  const levelDataMap = useMemo(
    () => ({
      province: { data: provinceData, nextLevel: 'cityData', setter: Step1Module2Operations.setSelectedProvince },
      city: { data: cityData, nextLevel: 'districtData', setter: Step1Module2Operations.setSelectedCity },
      district: {
        data: districtData,
        nextLevel: 'subDistrictData',
        setter: Step1Module2Operations.setSelectedDistrict
      },
      subdistrict: { data: subDistrictData, nextLevel: null, setter: Step1Module2Operations.setSelectedSubDistrict }
    }),
    [provinceData, cityData, districtData, subDistrictData]
  );

  const handleLocationChange = (e, level) => {
    const { value } = e.target;
    const { data, nextLevel, setter } = levelDataMap[level];

    if (value) {
      const infoLocation = data.find(item => item.location_id === value);
      if (nextLevel) {
        dispatch(Step1Module2Operations.getLocation(infoLocation.location_id, nextLevel));
      }
      dispatch(setter(infoLocation, 'cleanValues'));

      if (level === 'city') {
        getLocationWithoutSuggestion(infoLocation);
      } else if (level === 'district') {
        getLocationWithoutSuggestion(selectedCity, infoLocation);
      } else if (level === 'subdistrict') {
        getLocationWithoutSuggestion(selectedCity, selectedDistrict, infoLocation);
      }
    } else {
      dispatch(setter('', 'cleanValues'));
    }
    setIsEdited(true);
  };

  const onKeyDown = evt => {
    const code = evt.which ? evt.which : evt.keyCode;
    const dashCodes = [109, 189];
    if (!(isSpecialKey(code) || dashCodes.includes(code) || isKeyCodeNumber(code) || isKeyCodeNumpadNumber(code))) {
      evt.preventDefault();
    }
  };

  const handleSelect = (addr, placeId, placeData) => {
    const addressToSet = placeData?.description || addr;
    if (placeId) {
      handleSelectSuggestions(addressToSet, true, placeId);
    } else {
      handleUnSelectSuggestions(addr);
    }
  };

  if (loadError) {
    return 'Error loading maps';
  }
  if (!isLoaded) {
    return 'Loading Maps';
  }

  return (
    <Container data-repa='Step1Modulo2-locationSection-container'>
      <Title data-repa='Step1Modulo2-locationSection-cep-title'>{t('publisher.step1.module2.title')}</Title>
      {hasCep && (
        <CepContainer data-repa='Step1Modulo2-locationSection-cepContainer'>
          <InputTitle data-repa='Step1Modulo2-locationSection-inputTitle'>
            {t('publisher.step1.module2.cep.title')}
          </InputTitle>
          <InputCEP
            data-repa='Step1Modulo2-locationSection-inputCep'
            type='text'
            defaultValue={cepDefault}
            ref={register}
            key='s1m2cep'
            name='cep'
            autoComplete='off'
            maxLength='10'
            onKeyDown={onKeyDown}
            placeholder={t('publisher.step1.module2.cep.placeholder')}
            onBlur={() => {
              const cc = getValues('cep');
              // paliativo hasta refactor de paso ubicaciones
              // en los casos donde setValue no tiene efecto por cache
              if (cc && cc.indexOf('-') !== -1) {
                reset({ cep: formatCep(cc) });
              }
              setCep(cc);
            }}
          />
          {(cepError || (errors && errors.cep)) && !breakMobile && (
            <ErrorMessage>{t('publisher.step1.module2.cep.validation')}</ErrorMessage>
          )}
          <SearchButton
            data-repa='Step1Modulo2-locationSection-cepContainer-button'
            type='button'
            onClick={async () => {
              // temp fix to avoid race condition
              await new Promise(resolve => setTimeout(resolve, 1000));
              const valid = await trigger('cep', { shouldFocus: true });
              if (valid) {
                getCep();
              }
            }}
          >
            {t('publisher.btn.search')}
            <Icon name='location' width={12} height={12} viewBox='0 0 16 16' colorFill='#000000' />
          </SearchButton>
          {(cepError || (errors && errors.cep)) && breakMobile && (
            <ErrorMessage data-repa='Step1Modulo2-locationSection-cepContainer-error'>
              {t('publisher.step1.module2.cep.validation')}
            </ErrorMessage>
          )}
        </CepContainer>
      )}
      <AddressFieldsContainer>
        <AddressContainer>
          <InputTitle data-repa='Step1Modulo2-locationSection-address-title'>
            {t('publisher.step1.module2.address.title')}
          </InputTitle>
          <div data-repa='Step1Modulo2-locationSection-address-container'>
            <Address
              placeholder={t('publisher.step1.module2.address.placeholder')}
              autoFocus={false}
              name='addressVal'
              ref={register}
              value={getValues('addressVal') ? getValues('addressVal') : addressValue}
              autoComplete='nope'
              onFocus={() => setIsAddressFocused(true)}
              onBlur={() => {
                if (skipBlurRef.current) return;
                setIsAddressFocused(false);
                handleSelectSuggestions(getValues('addressVal'));
              }}
              onChange={e => {
                const value = e.target.value;
                setAddressValidation(null);
                setGetSuggestions(true);
                setAddressValue(value);
                setValue('addressVal', value);
                searchPlaces(value).then(({ status }) => {
                  if (status === 'ZERO_RESULTS') {
                    handleOnError(status, () => setGetSuggestions(false));
                  }
                });
              }}
              data-repa='Step1Modulo2-locationSection-address-location'
            />
            {addressValidation}
            <ErrorMessage
              data-repa='Step1Modulo2-locationSection-address-validationError'
              errors={errors}
              name='addressVal'
              as='div'
            >
              {errors.addressVal && <p>{t('publisher.step1.module2.address.validation')}</p>}
            </ErrorMessage>
            {placesReady && isAddressFocused && getSuggestions && predictions.length > 0 && (
              <PlacesSuggestions data-repa='location-address-suggestion'>
                {placesLoading && (
                  <div data-repa='Step1Modulo2-locationSection-suggestion-loading' className='suggestion-item'>
                    loading....
                  </div>
                )}
                {predictions.map((prediction, index) => {
                  const className = activeIndex === index ? 'suggestion-item active' : 'suggestion-item';
                  return (
                    <div
                      data-repa={`location-suggestion-${index}`}
                      className={className}
                      key={`${prediction.place_id}-${index}`}
                      onMouseEnter={() => setActiveIndex(index)}
                      onMouseLeave={() => setActiveIndex(null)}
                      onMouseDown={() => {
                        skipBlurRef.current = true;
                        handleSelect(prediction.description, prediction.place_id, prediction);
                        setIsAddressFocused(false);
                        setTimeout(() => {
                          skipBlurRef.current = false;
                        }, 0);
                      }}
                      onTouchStart={() => {
                        skipBlurRef.current = true;
                        handleSelect(prediction.description, prediction.place_id, prediction);
                        setIsAddressFocused(false);
                        setTimeout(() => {
                          skipBlurRef.current = false;
                        }, 0);
                      }}
                    >
                      {prediction.description}
                    </div>
                  );
                })}
              </PlacesSuggestions>
            )}
          </div>
        </AddressContainer>
        {site.isBrasil && (
          <ComplementContainer>
            <InputTitle data-repa='Step1Modulo2-locationSection-complement-title'>
              {t('publisher.step1.module2.complement.title')}
            </InputTitle>
            <InputComplement
              type='text'
              name='addressComplement'
              ref={register}
              value={addressComplement}
              placeholder={t('publisher.step1.module2.complement.placeholder')}
              onChange={e => {
                setAddressComplement(e.target.value);
              }}
              autoComplete='off'
            />
            <ErrorMessage
              data-repa='Step1Modulo2-locationSection-complement-validationError'
              errors={errors}
              name='addressComplement'
              as='div'
            >
              {errors.addressComplement && <p>{t(errors.addressComplement.message)}</p>}
            </ErrorMessage>
          </ComplementContainer>
        )}
      </AddressFieldsContainer>

      <input
        type='hidden'
        data-repa='Step1Modulo2-location-address-coord'
        name='coordinates'
        ref={register}
        value={[hasCoordinates.lat, hasCoordinates.lng]}
      />
      <LocationCombosContainer>
        <ComboLocation
          title={t('publisher.step1.module2.province.title')}
          onInput={e => handleLocationChange(e, 'province')}
          value={selectedProvince && selectedProvince.location_id}
          disabled={!address && !cep}
          name='provinceVal'
          id='provinceSelect'
          register={register}
          infoRepa='location-province'
          placeholder={t('publisher.step1.module2.province.placeholder')}
          locationData={provinceData}
          errors={errors.provinceVal}
          validationText={t('publisher.step1.module2.province.validation')}
        />
        <ComboLocation
          title={t('publisher.step1.module2.city.title')}
          onInput={e => handleLocationChange(e, 'city')}
          value={selectedCity && selectedCity.location_id}
          disabled={!selectedProvince || !selectedProvince.location_id}
          name='cityVal'
          id='citySelect'
          register={register}
          infoRepa='location-city'
          placeholder={t('publisher.step1.module2.city.placeholder')}
          locationData={cityData}
          errors={!errors.provinceVal && errors.cityVal}
          validationText={t('publisher.step1.module2.city.validation')}
        />
      </LocationCombosContainer>
      {districtData.length !== 0 && (
        <LocationCombosContainer>
          <ComboLocation
            title={t('publisher.step1.module2.district.title')}
            onInput={e => handleLocationChange(e, 'district')}
            value={selectedDistrict && selectedDistrict.location_id}
            disabled={!selectedCity || districtData.length === 0}
            name='districtVal'
            id='districtSelect'
            register={register}
            infoRepa='location-district'
            placeholder={t('publisher.step1.module2.district.placeholder')}
            locationData={districtData}
            errors={errors.districtVal}
            validationText={t('publisher.step1.module2.district.validation')}
          />
          {!showSubDistrict && (
            <ComboLocation
              title={t('publisher.step1.module2.subdistrict.title')}
              onInput={e => handleLocationChange(e, 'subdistrict')}
              value={selectedSubDistrict && selectedSubDistrict.location_id}
              disabled={!subDistrictData || subDistrictData.length === 0}
              name='subDistrictVal'
              id='subDistrictSelect'
              register={register}
              infoRepa='location-subDistrict'
              placeholder={t('publisher.step1.module2.subdistrict.placeholder')}
              locationData={subDistrictData}
              errors={errors.subDistrictVal}
              validationText={t('publisher.step1.module2.subdistrict.validation')}
            />
          )}
        </LocationCombosContainer>
      )}
    </Container>
  );
};
